import { useEffect, useState } from 'react';
import { apiClient, ApiError, type PublishResult, type ScrubPreview, type ScrubFinding } from '../lib/api';

interface PublishDialogProps {
  skillId: string;
  onClose: () => void;
  onPublished: (result: PublishResult) => void;
}

const TOKEN_STORAGE_KEY = 'skillsou.github.token';

type Step = 'review' | 'confirm';

export function PublishDialog({ skillId, onClose, onPublished }: PublishDialogProps) {
  const [step, setStep] = useState<Step>('review');
  const [preview, setPreview] = useState<ScrubPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [token, setToken] = useState(
    typeof window !== 'undefined' ? (localStorage.getItem(TOKEN_STORAGE_KEY) ?? '') : '',
  );
  const [rememberToken, setRememberToken] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Load scrub preview on open
  useEffect(() => {
    let cancelled = false;
    apiClient.publish
      .scrubPreview(skillId)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPreviewError(err instanceof ApiError ? err.message : 'Failed to load preview');
      });
    return () => {
      cancelled = true;
    };
  }, [skillId]);

  const blocks = preview?.block_reasons ?? [];
  const warnings = preview?.warnings ?? [];
  const placeholderCount = preview?.placeholder_count ?? 0;
  const hasBlockingFindings = blocks.length > 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPublishError(null);
    if (!token.trim()) {
      setPublishError('Provide a GitHub personal access token (gist scope).');
      return;
    }
    setBusy(true);
    try {
      const result = await apiClient.publish.toGithub(skillId, {
        target: 'gist',
        token: token.trim(),
        description: description.trim() || undefined,
        isPublic,
        confirmedScrub: true,
      });
      if (rememberToken) localStorage.setItem(TOKEN_STORAGE_KEY, token.trim());
      onPublished(result);
    } catch (err) {
      setPublishError(err instanceof ApiError ? err.message : 'Publish failed');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="w-full max-w-lg rounded-lg border bg-canvas p-5 text-sm"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,.18)' }}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">
            {step === 'review' ? 'Review what will be published' : 'Publish to GitHub gist'}
          </h2>
          <span className="text-[10px] uppercase tracking-wider text-text-subtle">
            Step {step === 'review' ? '1' : '2'} of 2
          </span>
        </div>

        {step === 'review' && (
          <>
            <p className="mt-1 text-[12px] text-text-subtle">
              Skillsou auto-scrubs known credential shapes. Review the findings before
              publishing.
            </p>

            {previewError && (
              <p className="mt-3 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
                {previewError}
              </p>
            )}

            {preview && (
              <>
                <div className="mt-4 rounded border bg-surface p-3">
                  <p className="text-[10px] uppercase tracking-wider text-text-subtle">
                    Summary
                  </p>
                  <ul className="mt-2 space-y-1 text-[12px]">
                    <li className="flex items-center gap-2">
                      <span
                        className="h-[6px] w-[6px] rounded-full"
                        style={{ background: 'var(--color-success)' }}
                      />
                      <span>
                        {placeholderCount} secret
                        {placeholderCount === 1 ? '' : 's'} replaced with placeholders.
                      </span>
                    </li>
                    {warnings.map((w, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span
                          className="h-[6px] w-[6px] rounded-full"
                          style={{ background: 'var(--color-warning)' }}
                        />
                        <span>{w}</span>
                      </li>
                    ))}
                    {blocks.map((b, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span
                          className="h-[6px] w-[6px] rounded-full"
                          style={{ background: 'var(--color-danger)' }}
                        />
                        <span className="text-[var(--color-danger)]">{b}</span>
                      </li>
                    ))}
                    {placeholderCount === 0 && warnings.length === 0 && blocks.length === 0 && (
                      <li className="text-text-subtle">No secrets detected.</li>
                    )}
                  </ul>
                </div>

                {preview.findings.length > 0 && (
                  <details className="mt-3 rounded border bg-surface p-3 text-[12px]">
                    <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-text-subtle">
                      {preview.findings.length} finding
                      {preview.findings.length === 1 ? '' : 's'} (click to expand)
                    </summary>
                    <ul className="mt-2 space-y-1 font-mono text-[11px]">
                      {preview.findings.map((f, i) => (
                        <FindingRow key={i} f={f} />
                      ))}
                    </ul>
                  </details>
                )}

                <details className="mt-3 rounded border bg-surface p-3 text-[12px]">
                  <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-text-subtle">
                    Preview scrubbed body
                  </summary>
                  <pre className="mt-2 max-h-[300px] overflow-y-auto whitespace-pre-wrap font-mono text-[11px]">
                    {preview.scrubbed_body}
                  </pre>
                </details>
              </>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="btn">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStep('confirm')}
                disabled={!preview}
                className="btn btn-primary"
              >
                {hasBlockingFindings ? 'Confirm and continue →' : 'Looks good →'}
              </button>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <form onSubmit={submit} className="mt-2">
            <p className="text-[12px] text-text-subtle">
              The published gist will carry a header with canonical id, version, and
              "scrubbed: {placeholderCount}" so the recipient knows it's intentional.
            </p>

            <label className="mt-4 block text-[10px] uppercase tracking-wider text-text-subtle">
              GitHub personal access token (gist scope only)
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="ghp_…"
                className="mt-1 block w-full rounded border bg-surface px-2 py-1.5 font-mono text-[12px] outline-none focus:border-[var(--color-accent)]"
                autoFocus
              />
            </label>

            <label className="mt-3 block text-[10px] uppercase tracking-wider text-text-subtle">
              Description (optional)
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 block w-full rounded border bg-surface px-2 py-1.5 text-[12px] outline-none focus:border-[var(--color-accent)]"
              />
            </label>

            <div className="mt-3 space-y-1.5">
              <label className="flex items-center gap-2 text-[12px] text-text-muted">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
                Public gist
              </label>
              <label className="flex items-center gap-2 text-[12px] text-text-muted">
                <input
                  type="checkbox"
                  checked={rememberToken}
                  onChange={(e) => setRememberToken(e.target.checked)}
                />
                Remember token in this browser
              </label>
            </div>

            {publishError && (
              <p className="mt-3 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
                {publishError}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setStep('review')} className="btn">
                ← Back
              </button>
              <button type="submit" disabled={busy} className="btn btn-primary">
                {busy ? 'Publishing…' : 'Publish'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const CATEGORY_LABELS: Record<ScrubFinding['category'], string> = {
  declared_secret: 'declared secret',
  openai_key: 'OpenAI key',
  anthropic_key: 'Anthropic key',
  github_pat: 'GitHub PAT',
  aws_key: 'AWS key',
  generic_high_entropy: 'high-entropy string',
  user_info_url: 'URL with user:pass',
  email: 'email address',
};

function FindingRow({ f }: { f: ScrubFinding }) {
  const sevColor =
    f.severity === 'block'
      ? 'var(--color-danger)'
      : f.severity === 'warn'
        ? 'var(--color-warning)'
        : 'var(--color-success)';
  return (
    <li className="flex items-center gap-2">
      <span className="h-[6px] w-[6px] rounded-full" style={{ background: sevColor }} />
      <span className="text-text-subtle">{CATEGORY_LABELS[f.category]}</span>
      {f.placeholderName && (
        <span className="text-text-muted">→ ${'{'}{f.placeholderName}{'}'}</span>
      )}
      {!f.placeholderName && (
        <span className="truncate text-text-muted">
          {f.match.length > 40 ? `${f.match.slice(0, 40)}…` : f.match}
        </span>
      )}
    </li>
  );
}
